import React from 'react';
import LinkArrow from '../../global/LinkArrow';
import Lottie from 'react-lottie';


interface HighlightItem {
  title: string;
  description: string;
  link: {
    title: string;
    href: string;
  };
  image?: string;
  lottie?: any;
  imageDescription?: string;
}

interface HighlightsSectionProps {
  preTitle: string;
  title: string;
  items: readonly HighlightItem[];
}

export const HighlightsSection = ({
  preTitle,
  title,
  items,
}: HighlightsSectionProps) => {
  const getLottieOptions = (lottieData: any) => ({
    loop: true,
    autoplay: true,
    animationData: lottieData,
  });

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <p className="text-orange mb-4 text-xs lg:text-sm">{preTitle}</p>
          <h2 className="text-2xl lg:text-5xl font-medium">{title}</h2>
        </div>
        <div className="flex justify-center overflow-hidden w-full h-full">
          <svg
            width="1260"
            height="2"
            viewBox="0 0 1260 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 1H1260" stroke="url(#paint0_linear_2413_18744)" />
            <defs>
              <linearGradient
                id="paint0_linear_2413_18744"
                x1="0"
                y1="1.5"
                x2="1260"
                y2="1.49987"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#EAEAEA" stopOpacity="0.1" />
                <stop
                  offset="0.511666"
                  stopColor="#BCBCBC"
                  stopOpacity="0.543334"
                />
                <stop offset="1" stopColor="#EAEAEA" stopOpacity="0.1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 w-full my-2 gap-8">
          {items.map((item, index) => (
            <div key={index} className="col-span-1 relative">
              <div className="bg-gradient-to-b from-[#F7F7F7] to-white rounded-lg pt-8 lg:pt-0 flex flex-col justify-between h-full">
                <div className="flex flex-col lg:py-16 lg:px-12">
                  <h3 className="text-2xl lg:text-3xl mb-4 font-medium">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-base">
                    {item.description}
                  </p>
                  <LinkArrow to={item.link.href} color="orange" size="md">
                    {item.link.title}
                  </LinkArrow>
                </div>
                {item?.lottie ? (
                  <div className="relative w-full flex justify-end items-end">
                    <Lottie options={getLottieOptions(item.lottie)} />
                    {item?.imageDescription && (
                      <div
                        className="absolute bottom-0 w-full text-center text-sm"
                        dangerouslySetInnerHTML={{
                          __html: item.imageDescription,
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="relative w-full flex justify-end items-end">
                    <img src={item.image} alt={item.title} />
                    {item?.imageDescription && (
                      <div
                        className="absolute bottom-0 w-full text-center text-sm"
                        dangerouslySetInnerHTML={{
                          __html: item.imageDescription,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              {index % 2 === 0 && index !== items.length - 1 && (
                <>
                  <div className="flex justify-center md:hidden relative w-full mt-8 overflow-hidden">
                    <svg
                      height="2"
                      viewBox="0 0 1260 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 1H1260"
                        stroke="url(#paint0_linear_2413_18745)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2413_18745"
                          x1="0"
                          y1="1.5"
                          x2="1260"
                          y2="1.49987"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#EAEAEA" stopOpacity="0.1" />
                          <stop
                            offset="0.511666"
                            stopColor="#BCBCBC"
                            stopOpacity="0.543334"
                          />
                          <stop
                            offset="1"
                            stopColor="#EAEAEA"
                            stopOpacity="0.1"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="md:block hidden absolute top-0 -right-4">
                    <svg
                      width="2"
                      height="599"
                      viewBox="0 0 2 599"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 0L1.00003 599"
                        stroke="url(#paint0_linear_2413_18738)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2413_18738"
                          x1="0.5"
                          y1="2.18557e-08"
                          x2="0.500055"
                          y2="599"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#EAEAEA" stopOpacity="0.1" />
                          <stop
                            offset="0.511666"
                            stopColor="#BCBCBC"
                            stopOpacity="0.543334"
                          />
                          <stop
                            offset="1"
                            stopColor="#EAEAEA"
                            stopOpacity="0.1"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
